import React from 'react';
import { useInkind } from '@gik/api/inkinds/inkind';
import bemBlock from '@gik/core/utils/bemBlock';
import { inkindPageTourSelectors, openInkindPageSettingsModal } from '@gik/inkind-page';
import {
  AnimatedDivBone,
  Avatar,
  BackgroundImage,
  Button,
  Container,
  Decoder,
  LightboxMedia,
  openImagePickerModal,
  ParagraphBone,
  SvgIcon,
  Tooltip,
  UI,
} from '@gik/ui';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import InkindPagePrivacyBadges from './InkindPagePrivacyBadges';
import { Breakpoint, useBreakpoint } from '@gik/core/hooks/hooks/BreakpointHooks';
import InkindCoverToolbar from './InkindCoverToolbar';
import { attachCoverPhotoImage, ICoverPhotoEditor } from './CoverPhotoEditor';
import { uploadInkindCoverPhoto } from '@gik/api/inkinds/inkindCoverPhoto';
import sleep from '@gik/core/utils/sleep';
import { useIsRetina } from '@gik/core/hooks/CustomHooks';
import { usePrevious } from 'react-use';
import { openContactPageOrganizersModal } from '@gik/calendar';
import { updatePagePhoto } from '@gik/inkind-page/api/InkindPageAPI';
import InkindCoverUploadImageControls from './InkindCoverUploadImageControls';
import { useUserStore } from '@gik/core/store/UserStore';
import { canUserEditPage } from '@gik/inkind-page/utils/inkindPagePermissions';
import FollowPageCTA from '@gik/inkind-page/components/FollowPageCTA/FollowPageCTA';

interface IInkindPageHeaderProps {
  inkindRouteId: string;
}

let coverPhotoEditor: ICoverPhotoEditor;
export default function InkindPageHeader({ inkindRouteId }: IInkindPageHeaderProps) {
  const { data: inkindPage, revalidate: revalidateInkindPage } = useInkind(inkindRouteId);
  const bem = bemBlock('inkind-page-header');
  const isMdDown = useBreakpoint(Breakpoint.MD_DOWN);
  const { t } = useTranslation();
  const userId = useUserStore(state => state.id);

  const canEdit = canUserEditPage(inkindPage, userId);
  const isRetina = useIsRetina();

  // TODO: move to hook
  const [isEditingCoverPhoto, setEditingCoverPhoto] = React.useState(false);
  const [isSavingCoverPhoto, setSavingCoverPhoto] = React.useState(false);
  const [isChangingCoverColour, setChangingCoverColour] = React.useState(false);
  const coverColourIndex = inkindPage ? inkindPage.coverColourIndex : 0;
  const previousCoverColourIndex = usePrevious(coverColourIndex);

  async function onEditCoverPhoto() {
    coverPhotoEditor = await attachCoverPhotoImage();
    if (coverPhotoEditor) {
      // file was attached and processed successfully, launch edit mode
      setEditingCoverPhoto(true);
    }
  }

  function closeCoverPhotoEditor() {
    if (coverPhotoEditor) {
      coverPhotoEditor.destroy();
      coverPhotoEditor = null;
    }
    setEditingCoverPhoto(false);
  }

  function onCancelUploadCoverPhoto() {
    closeCoverPhotoEditor();
  }

  async function onCoverColourChange() {
    setChangingCoverColour(true);
    await sleep(1000);
    setChangingCoverColour(false);
  }

  async function onUploadCoverPhoto() {
    if (coverPhotoEditor) {
      const base64CoverPhotoImage = coverPhotoEditor.renderOutputImage();
      try {
        setSavingCoverPhoto(true);
        coverPhotoEditor.destroy();
        await uploadInkindCoverPhoto(inkindRouteId, base64CoverPhotoImage);
        await revalidateInkindPage();
        // wait for the new image to load
        await sleep(2000);
        closeCoverPhotoEditor();
      } catch (error) {
        console.error(error);
        UI.error('Failed to upload cover image. Please try again');
      }
      setSavingCoverPhoto(false);
    }
  }

  const organizers = inkindPage ? [inkindPage.owner, ...inkindPage.organizers] : [];

  function openOrganizersModal() {
    openContactPageOrganizersModal(inkindRouteId);
  }

  function openPageImageLightbox() {
    UI.lightbox([
      {
        mediaType: LightboxMedia.IMAGE,
        url: inkindPage.imgSrc,
        caption: inkindPage.title,
      },
    ]);
  }

  async function handleChangeCroppedImage(croppedImage: string) {
    await updatePagePhoto(inkindPage.routeId, croppedImage);
    revalidateInkindPage();
  }

  function openChangePageImageModal() {
    openImagePickerModal({
      title: t(translationKeys.PageHeaderChangePageImageModalTitle),
      text: t(translationKeys.PageHeaderChangePageImageModalImagePickerText),
      fallback: inkindPage.imgSrc,
      onChange: handleChangeCroppedImage,
    });
  }

  const settingsSection = (
    <section className={bem('settings')}>
      {!isEditingCoverPhoto && canEdit && (
        <InkindCoverToolbar
          inkindRouteId={inkindRouteId}
          onClickAddCoverPhoto={onEditCoverPhoto}
          onCoverColourChange={onCoverColourChange}
        />
      )}
      {!isEditingCoverPhoto && canEdit && (
        <Button
          id={inkindPageTourSelectors.pageSettings}
          variant="secondary"
          pill
          className={bem('btn-search')}
          onClick={() => openInkindPageSettingsModal({ inkindRouteId })}
          prepend={<SvgIcon name="Cog" />}
        >
          {t(translationKeys.PageHeaderPageSettings)}
        </Button>
      )}
      <FollowPageCTA inkindRouteId={inkindRouteId} />
    </section>
  );

  const privacySection = (
    <section className={bem('privacy', [{ 'transparent-preview': isEditingCoverPhoto }])}>
      <InkindPagePrivacyBadges inkindRouteId={inkindRouteId} />
    </section>
  );

  const mainSection = (
    <section className={bem('main', [{ 'transparent-preview': isEditingCoverPhoto }])}>
      <aside className={bem('avatar-wrapper')}>
        {canEdit && (
          <div className={bem('change-inkind-photo')}>
            <Button variant={'secondary'} circle onClick={openChangePageImageModal}>
              <SvgIcon name={'Camera'} />
            </Button>
          </div>
        )}

        {inkindPage ? (
          <Avatar
            className={bem('inkind-thumbnail')}
            imageUrl={`${inkindPage?.thumb_medium}`}
            circle={false}
            onClick={openPageImageLightbox}
          />
        ) : (
          <AnimatedDivBone className={bem('inkind-thumbnail')} />
        )}
      </aside>
      <main className={bem('header-info')}>
        <section className={bem('info-wrapper')}>
          <h1 className={bem('page-title')}>
            {inkindPage ? <Decoder text={inkindPage.title} /> : <ParagraphBone words={5} />}
          </h1>
          {!inkindPage ? (
            <h2>
              <ParagraphBone words={4} />
            </h2>
          ) : (
            inkindPage.city &&
            inkindPage.stateCode && (
              <h2 className={bem('page-location')}>
                {inkindPage.city}, {inkindPage.stateCode}
              </h2>
            )
          )}
        </section>
        <section className={bem('organizers-section')}>
          {inkindPage ? (
            <>
              <label className={bem('organizers-label')}>{t(translationKeys.organizersTitle)}</label>
              {organizers.map(organizer => (
                <Tooltip key={organizer.userId} text={organizer.name}>
                  <Avatar
                    className={bem('organizer-avatar')}
                    variant={'primary'}
                    imageUrl={organizer.imgThumbSmall}
                    showBorder
                    onClick={openOrganizersModal}
                  />
                </Tooltip>
              ))}
            </>
          ) : (
            <ParagraphBone className={'tw-mb-2'} words={2} />
          )}
        </section>
      </main>
    </section>
  );

  return (
    <div className={bem()}>
      <div className={bem('inner')} id="inkind-cover-content">
        {inkindPage && (
          <>
            <div className={bem('cover-gradient', undefined, `gradient${coverColourIndex}`)} />
            <div
              className={bem(
                'cover-gradient-prev',
                [{ changing: isChangingCoverColour }],
                `gradient${previousCoverColourIndex}`
              )}
            />
          </>
        )}
        {inkindPage?.cover_src_retina && (
          <BackgroundImage
            className={bem('cover-photo')}
            src={isRetina ? inkindPage.cover_src_retina : inkindPage.cover_src}
          />
        )}
        <div className={bem('cover-photo-editor', [{ open: isEditingCoverPhoto }])} id="inkind-cover-photo-editor">
          <img id="inkind-cover-photo-editor-image" />
        </div>

        <Container className={bem('content')}>
          {isEditingCoverPhoto && (
            <InkindCoverUploadImageControls
              isSaving={isSavingCoverPhoto}
              onCancel={onCancelUploadCoverPhoto}
              onSave={onUploadCoverPhoto}
              direction={coverPhotoEditor?.direction}
            />
          )}
          {isMdDown ? (
            <>
              {settingsSection}
              {mainSection}
              {privacySection}
            </>
          ) : (
            <main className={bem('desktop-layout')}>
              {mainSection}
              <aside>
                {settingsSection}
                {privacySection}
              </aside>
            </main>
          )}
        </Container>
      </div>
    </div>
  );
}
